import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Modal, Form, Input, Select, App, Steps, Card, Tag, Button, Space, Alert } from 'antd';
import { UserOutlined, MailOutlined, LockOutlined, SearchOutlined } from '@ant-design/icons';
import logger from '../../utils/logger';
import { getApiErrorMessage } from '../../utils/apiError';

const { Option } = Select;
const { Step } = Steps;
const { Search } = Input;

const UserFormModal = ({ open, onClose, onSubmit, user, tenants, isSuperAdmin, currentUser }) => {
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [createdUsers, setCreatedUsers] = useState([]);

  const isEditMode = !!user;
  const isImpersonating = currentUser?.impersonating || false;
  const impersonatedTenantId = currentUser?.tenant_id;
  const isTenantAdmin = currentUser?.role === 'tenant_admin';
  const tenantAdminTenantId = isTenantAdmin ? currentUser?.tenant_id : null;

  // Filter tenants based on search
  const filteredTenants = tenants?.filter(tenant =>
    tenant.name?.toLowerCase().includes(searchText.toLowerCase()) ||
    tenant._id?.toLowerCase().includes(searchText.toLowerCase()) ||
    tenant.id?.toLowerCase().includes(searchText.toLowerCase()) ||
    tenant.tier?.toLowerCase().includes(searchText.toLowerCase())
  ) || [];

  // Reset state when modal opens/closes
  useEffect(() => {
    if (open) {
      if (isEditMode && user) {
        // Edit mode - skip step 1, go straight to user form
        setCurrentStep(1);
        const userTenant = tenants?.find(t => t.id === user.tenant_id || t._id === user.tenant_id);
        setSelectedTenant(userTenant || null);

        form.setFieldsValue({
          email: user.email,
          first_name: user.first_name,
          last_name: user.last_name,
          role: user.role,
          tenant_id: user.tenant_id || '',
        });
      } else if (isImpersonating || isTenantAdmin) {
        // Impersonation mode or Tenant Admin - auto-select tenant and skip to user form
        setCurrentStep(1);
        const lockedTenantId = isImpersonating ? impersonatedTenantId : tenantAdminTenantId;
        const lockedTenant = tenants?.find(t =>
          t.id === lockedTenantId ||
          t._id === lockedTenantId ||
          t.tenant_id === lockedTenantId
        );
        setSelectedTenant(lockedTenant || null);
        setCreatedUsers([]);
        form.resetFields();
        form.setFieldsValue({
          tenant_id: lockedTenant?.tenant_id || lockedTenant?.id || lockedTenant?._id || lockedTenantId
        });
        setSearchText('');
      } else {
        // Create mode - start at step 0 (tenant selection)
        setCurrentStep(0);
        setSelectedTenant(null);
        setCreatedUsers([]);
        form.resetFields();
        setSearchText('');
      }
    }
  }, [open, user, isEditMode, isImpersonating, impersonatedTenantId, isTenantAdmin, tenantAdminTenantId, form, tenants]);

  const handleTenantSelect = (tenant) => {
    setSelectedTenant(tenant);
  };

  const handleConfirmTenant = () => {
    if (selectedTenant) {
      setCurrentStep(1);
      // Pre-fill tenant_id in form
      form.setFieldsValue({ tenant_id: selectedTenant.tenant_id || selectedTenant._id || selectedTenant.id });
    }
  };

  const handleBack = () => {
    // Don't allow going back when impersonating or tenant_admin
    if (currentStep === 1 && !isEditMode && !isImpersonating && !isTenantAdmin) {
      setCurrentStep(0);
      setSelectedTenant(null);
      setSearchText('');
    }
  };

  const handleSubmitUser = async (closeAfter = true) => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      await onSubmit(values);

      if (closeAfter) {
        // Close modal
        message.success(isEditMode ? 'User updated successfully' : 'User created successfully');
        form.resetFields();
        setCurrentStep(0);
        setSelectedTenant(null);
        setSearchText('');
        setCreatedUsers([]);
        onClose();
      } else {
        // Create another user - add to list and reset form
        message.success(`User "${values.first_name} ${values.last_name}" created successfully!`);

        const newUser = {
          email: values.email,
          first_name: values.first_name,
          last_name: values.last_name,
          role: values.role
        };
        setCreatedUsers(prev => [...prev, newUser]);

        // Reset form but keep tenant selection
        form.resetFields();
        form.setFieldsValue({
          tenant_id: selectedTenant.tenant_id || selectedTenant._id || selectedTenant.id,
          role: 'user' // Reset to default role
        });

        // Don't call onClose() here - keep modal open
      }
    } catch (error) {
      logger.error('Failed to save user:', error);
      if (error.errorFields) {
        message.error('Please check the form for errors');
      } else {
        // Rendered as plain text by antd; never as markup — a server error detail
        // is attacker-influencable text (SEC-1-1).
        message.error(
          getApiErrorMessage(error, `Failed to ${isEditMode ? 'update' : 'create'} user. Please try again.`)
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setCurrentStep(0);
    setSelectedTenant(null);
    setSearchText('');
    setCreatedUsers([]);
    onClose();
  };

  // Get tier badge color
  const getTierColor = (tier) => {
    const tierLower = tier?.toLowerCase() || '';
    if (tierLower.includes('enterprise') || tierLower === 'tier_3') return 'gold';
    if (tierLower.includes('professional') || tierLower === 'tier_2') return 'blue';
    if (tierLower.includes('basic') || tierLower === 'tier_1') return 'green';
    return 'default';
  };

  // Get tier display name
  const getTierName = (tier) => {
    if (!tier) return 'N/A';
    if (tier === 'tier_1') return 'Basic';
    if (tier === 'tier_2') return 'Professional';
    if (tier === 'tier_3') return 'Enterprise';
    return tier;
  };

  // Render Step 1: Select Tenant
  const renderTenantSelection = () => (
    <div>
      <Alert
        message="Select a Tenant"
        description="Choose which tenant this user will belong to. You can search by tenant name, ID, or tier."
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />

      <Search
        placeholder="Search tenants by name, ID, or tier..."
        allowClear
        size="large"
        prefix={<SearchOutlined />}
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        style={{ marginBottom: 16 }}
      />

      <div style={{ maxHeight: 400, overflowY: 'auto', border: '1px solid var(--rlc-surface-muted)', borderRadius: '4px' }}>
        {filteredTenants.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: 'var(--rlc-text-muted)' }}>
            {searchText ? 'No tenants found matching your search' : 'No tenants available'}
          </div>
        ) : (
          filteredTenants.map(tenant => {
            // Get unique identifiers for both selected and current tenant
            const selectedId = selectedTenant?.tenant_id || selectedTenant?._id || selectedTenant?.id;
            const tenantId = tenant.tenant_id || tenant._id || tenant.id;
            const isSelected = selectedTenant && selectedId === tenantId;

            return (
              <Card
                key={tenant._id || tenant.id}
                hoverable
                onClick={() => handleTenantSelect(tenant)}
                style={{
                  marginBottom: 8,
                  cursor: 'pointer',
                  border: isSelected ? '3px solid var(--rlc-state-info)' : '1px solid var(--rlc-surface-muted)',
                  borderRadius: '4px',
                  backgroundColor: isSelected ? 'var(--rlc-surface-accent)' : 'white',
                  boxShadow: isSelected ? '0 4px 12px rgba(6, 62, 68, 0.15)' : 'none'
                }}
                bodyStyle={{ padding: '12px 16px' }}
              >
              <Space direction="vertical" size={4} style={{ width: '100%' }}>
                <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                  <span style={{ fontWeight: 600, fontSize: 15 }}>{tenant.name}</span>
                  <Tag color={getTierColor(tenant.tier)}>{getTierName(tenant.tier)}</Tag>
                </Space>
                <div style={{ fontSize: 13, color: 'var(--rlc-text-secondary)' }}>
                  <span>ID: </span>
                  <span style={{ fontFamily: 'monospace', background: 'var(--rlc-surface-sunken)', padding: '2px 6px', borderRadius: '3px' }}>
                    {tenant._id || tenant.id}
                  </span>
                </div>
                {tenant.email && (
                  <div style={{ fontSize: 12, color: 'var(--rlc-text-muted)' }}>{tenant.email}</div>
                )}
              </Space>
            </Card>
            );
          })
        )}
      </div>
    </div>
  );

  // Render Step 2: Create User Form
  const renderUserForm = () => (
    <div>
      {/* Show tenant locked warning for impersonation or tenant_admin */}
      {(isImpersonating || isTenantAdmin) && selectedTenant && (
        <Alert
          message={isImpersonating ? "Tenant Locked (Impersonation Mode)" : "Tenant Locked (Tenant Admin)"}
          description={
            <div>
              {isImpersonating ? (
                <>You are impersonating <strong>{selectedTenant.name}</strong>. Users created will automatically be assigned to this tenant.</>
              ) : (
                <>As a Tenant Admin, you can only create users in your tenant: <strong>{selectedTenant.name}</strong>.</>
              )}
            </div>
          }
          type={isImpersonating ? "warning" : "info"}
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      {/* Show selected tenant at top - MORE PROMINENT */}
      {selectedTenant && !isEditMode && !isImpersonating && (
        <Card style={{ marginBottom: 24, background: 'linear-gradient(135deg, var(--rlc-surface-inverse-alt) 0%, var(--rlc-surface-inverse) 100%)', border: 'none' }}>
          <div style={{ color: 'white' }}>
            <div style={{ fontSize: 13, marginBottom: 4 }}>Creating users for tenant:</div>
            <div style={{ fontSize: 20, fontWeight: 600 }}>{selectedTenant.name}</div>
            <div style={{ fontSize: 12, marginTop: 4, opacity: 0.9 }}>
              {getTierName(selectedTenant.tier)} Tier • ID: {selectedTenant._id || selectedTenant.id}
            </div>
          </div>
        </Card>
      )}

      {isEditMode && selectedTenant && (
        <Alert
          message={
            <Space>
              <span>Tenant:</span>
              <Tag color={getTierColor(selectedTenant.tier)}>{selectedTenant.name}</Tag>
            </Space>
          }
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      <Form
        form={form}
        layout="vertical"
        autoComplete="off"
      >
        <Form.Item
          label="Email"
          name="email"
          rules={[
            { required: true, message: 'Please enter email address' },
            { type: 'email', message: 'Please enter a valid email address' }
          ]}
          tooltip={isEditMode ? "Email cannot be changed" : undefined}
        >
          <Input
            prefix={<MailOutlined />}
            placeholder="jane.doe@example.com"
            disabled={isEditMode}
          />
        </Form.Item>

        <Form.Item
          label="First Name"
          name="first_name"
          rules={[{ required: true, message: 'Please enter first name' }]}
        >
          <Input
            prefix={<UserOutlined />}
            placeholder="Jane"
          />
        </Form.Item>

        <Form.Item
          label="Last Name"
          name="last_name"
          rules={[{ required: true, message: 'Please enter last name' }]}
        >
          <Input
            prefix={<UserOutlined />}
            placeholder="Doe"
          />
        </Form.Item>

        {!isEditMode && (
          <Form.Item
            label="Password"
            name="password"
            rules={[
              { required: true, message: 'Please enter password' },
              { min: 8, message: 'Password must be at least 8 characters' }
            ]}
            tooltip="Password must be at least 8 characters"
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="Minimum 8 characters"
            />
          </Form.Item>
        )}

        {isEditMode && (
          <div style={{
            background: 'var(--rlc-surface-accent)',
            padding: '12px',
            borderRadius: '4px',
            marginBottom: 16,
            fontSize: '13px',
            color: 'var(--rlc-text-secondary)'
          }}>
            💡 To change password, use the "Reset Password" action instead
          </div>
        )}

        <Form.Item
          label="Role"
          name="role"
          rules={[{ required: true, message: 'Please select a role' }]}
          initialValue="user"
        >
          <Select placeholder="Select role">
            <Option value="user">User</Option>
            {isSuperAdmin && (
              <>
                <Option value="tenant_admin">Tenant Admin</Option>
                <Option value="super_admin">Super Admin</Option>
              </>
            )}
          </Select>
        </Form.Item>

        {/* Hidden field to store tenant_id */}
        <Form.Item
          name="tenant_id"
          hidden
        >
          <Input />
        </Form.Item>
      </Form>

      {/* Show created users list */}
      {createdUsers.length > 0 && (
        <Card title={`Created Users (${createdUsers.length})`} style={{ marginTop: 24 }} size="small">
          <div style={{ maxHeight: 200, overflowY: 'auto' }}>
            {createdUsers.map((user, index) => (
              <div key={user.email} style={{
                padding: '8px 12px',
                borderBottom: index < createdUsers.length - 1 ? '1px solid var(--rlc-surface-muted)' : 'none',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <div>
                  <div style={{ fontWeight: 500 }}>{user.first_name} {user.last_name}</div>
                  <div style={{ fontSize: 12, color: 'var(--rlc-text-secondary)' }}>{user.email}</div>
                </div>
                <Tag color={user.role === 'super_admin' ? 'purple' : user.role === 'tenant_admin' ? 'blue' : 'green'}>
                  {user.role === 'super_admin' ? 'Super Admin' : user.role === 'tenant_admin' ? 'Tenant Admin' : 'User'}
                </Tag>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );

  // Custom footer based on current step
  const getFooter = () => {
    if (currentStep === 0) {
      // Step 0: Tenant selection - show Cancel and OK
      return (
        <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
          <Button key="cancel" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            key="ok"
            type="primary"
            onClick={handleConfirmTenant}
            disabled={!selectedTenant}
          >
            OK
          </Button>
        </div>
      );
    } else {
      // Step 1: User form
      return (
        <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
          {/* Left side - Back button (hidden when impersonating, tenant_admin, or editing) */}
          <div>
            {!isEditMode && !isImpersonating && !isTenantAdmin && (
              <Button key="back" onClick={handleBack}>
                Back
              </Button>
            )}
          </div>

          {/* Right side - action buttons */}
          <Space>
            <Button key="cancel" onClick={handleCancel}>
              Cancel
            </Button>

            {!isEditMode && (
              <Button
                key="create-another"
                type="default"
                loading={loading}
                onClick={() => handleSubmitUser(false)}
              >
                Create User & Add Another
              </Button>
            )}

            <Button
              key="submit"
              type="primary"
              loading={loading}
              onClick={() => handleSubmitUser(true)}
            >
              {isEditMode ? 'Update User' : 'Create User & Close'}
            </Button>
          </Space>
        </div>
      );
    }
  };

  return (
    <Modal
      title={isEditMode ? 'Edit User' : 'Create User'}
      open={open}
      onCancel={handleCancel}
      footer={getFooter()}
      width={600}
      destroyOnClose
    >
      {!isEditMode && !isImpersonating && !isTenantAdmin && (
        <Steps current={currentStep} style={{ marginBottom: 24 }} size="small">
          <Step title="Select Tenant" />
          <Step title="Create User(s)" />
        </Steps>
      )}

      {currentStep === 0 ? renderTenantSelection() : renderUserForm()}
    </Modal>
  );
};

UserFormModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  user: PropTypes.shape({
    email: PropTypes.string,
    first_name: PropTypes.string,
    last_name: PropTypes.string,
    role: PropTypes.string,
    tenant_id: PropTypes.string,
  }),
  tenants: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string,
      id: PropTypes.string,
      name: PropTypes.string,
      tier: PropTypes.string,
      email: PropTypes.string,
    })
  ),
  isSuperAdmin: PropTypes.bool,
  currentUser: PropTypes.shape({
    impersonating: PropTypes.bool,
    tenant_id: PropTypes.string,
  }),
};

export default UserFormModal;
